import { axiosInstance } from "./axiosInstance";

export const getChat = async (data: any) => {
	return await axiosInstance.post(`/api/feature/chatbot`, data);
}

// Helper: cek apakah pesan error mengandung kata teknis yang tidak boleh tampil ke user
const containsTechnicalWords = (message: string): boolean => {
	const lower = message.toLowerCase();
	return ["failed to fetch", "http", "error", "undefined"].some((word) => lower.includes(word));
};

// Helper: map status HTTP ke pesan error manusiawi, pakai pesan backend kalau sudah manusiawi
const getHumanErrorMessage = (status: number, backendMessage?: string): string => {
	if (backendMessage && !containsTechnicalWords(backendMessage)) {
		return backendMessage;
	}

	if (status === 408 || status === 429 || status >= 500) {
		return "Server Brodi lagi sibuk nih kak, coba lagi dalam beberapa saat ya.";
	}

	if (status === 401 || status === 403) {
		return "Sesi kamu sudah habis kak, coba login ulang dulu ya.";
	}

	return "Ada kendala teknis nih kak, coba lagi sebentar lagi ya.";
};

/** Event tool dari SSE Brodi (ToolCallStarted / ToolCallCompleted) */
export type ChatbotToolEvent = {
	type: "started" | "completed";
	tool_name: string;
	tool_call_id: string;
	success?: boolean;
};

/**
 * Fungsi untuk streaming chatbot response via SSE menggunakan fetch + ReadableStream
 * Menggunakan Authorization header sesuai logika axiosInstance (TOKEN/PARENT_TOKEN/TENTOR_TOKEN)
 *
 * @param data - { session_id, message }
 * @param onChunk - Callback dipanggil setiap token diterima
 * @param onComplete - Callback dipanggil saat stream selesai (DONE marker)
 * @param onError - Callback dipanggil saat error
 * @param signal - AbortSignal untuk cancel stream
 * @param onToolEvent - Callback ToolCallStarted / ToolCallCompleted
 */
export const getChatStream = async (
	data: { session_id: string; message: string },
	onChunk: (chunk: string) => void,
	onComplete: () => void,
	onError: (error: Error) => void,
	signal?: AbortSignal,
	onToolEvent?: (event: ChatbotToolEvent) => void
): Promise<void> => {
	// Ambil token sesuai logika axiosInstance (berdasarkan pathname)
	const isReportPath = window.location.pathname.includes("/report/");
	const isTentorPath = window.location.pathname.includes("/tentor/");
	const isLogoutRequest = false; // Chatbot bukan logout request

	let token: string | null = null;
	if (isReportPath && isLogoutRequest) {
		token = localStorage.getItem("TOKEN");
	} else if (isReportPath) {
		token = localStorage.getItem("PARENT_TOKEN");
	} else if (isTentorPath) {
		token = localStorage.getItem("TENTOR_TOKEN");
	} else {
		token = localStorage.getItem("TOKEN");
	}

	const apiUrl = process.env.REACT_APP_API_URL || "https://api-dev.brofesional.id";
	// const apiUrl = "https://franken-api.brofesional.id"; // Override untuk environment tertentu

	try {
		const response = await fetch(`${apiUrl}/api/feature/chatbot`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				...(token ? { Authorization: `Bearer ${token}` } : {}),
			},
			body: JSON.stringify(data),
			signal,
		});

		if (!response.ok) {
			// Fallback: coba parse error response dari backend dulu
			let backendMessage = "";
			try {
				const errorData = await response.json();
				backendMessage = errorData?.response || errorData?.message || "";
			} catch {
				// Ignore parse error, lanjut pakai mapping status
			}
			// Selalu tampilkan pesan manusiawi — jangan bocorkan pesan teknis (HTTP 504, Failed to fetch, dll)
			throw new Error(getHumanErrorMessage(response.status, backendMessage));
		}

		const contentType = response.headers.get("content-type") || "";
		const isStream = contentType.includes("text/event-stream");

		if (!isStream) {
			// Fallback non-stream: response JSON biasa
			const jsonData = await response.json();
			const fullText = jsonData?.response || jsonData?.data?.response || "Maaf, saya tidak mengerti";
			onChunk(fullText);
			onComplete();
			return;
		}

		// SSE Streaming parsing
		const reader = response.body?.getReader();
		if (!reader) {
			throw new Error("Response body tidak readable");
		}

		const decoder = new TextDecoder();
		let buffer = "";

		while (true) {
			const { done, value } = await reader.read();
			if (done) break;

			// Decode chunk dan tambahkan ke buffer
			// Normalisasi CRLF / CR ke LF (backend kadang kirim \r\n)
			buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n").replace(/\r/g, "\n");

			// Parse SSE events (dipisah oleh \n\n)
			const events = buffer.split("\n\n");
			// Simpan sisa yang mungkin incomplete ke buffer
			buffer = events.pop() || "";

			for (const event of events) {
				const lines = event.split("\n");
				let eventName = "";
				let dataStr = "";

				for (const line of lines) {
					// Dukung "event:" dan "event: " (SSE spec mengizinkan tanpa spasi setelah colon)
					if (line.startsWith("event:")) {
						// Track nama event (contoh: "content", "done", "ToolCallStarted")
						eventName = line.slice(6).trim();
					} else if (line.startsWith("data:")) {
						// Dukung "data:" dan "data: " (tanpa spasi setelah colon)
						dataStr = line.slice(5).trim();
					}
				}

				const eventKey = eventName.toLowerCase();

				// Event "done" menandakan akhir stream (format API asli)
				if (eventKey === "done" || dataStr === "[]") {
					onComplete();
					return;
				}

				// Event "error" dari backend (misal: data: {"message":"Brodi gagal memproses pesan."})
				// Jangan perlakukan sebagai token — panggil onError dan hentikan stream
				if (eventKey === "error") {
					let errorMessage = "Brodi gagal memproses pesan.";
					if (dataStr) {
						try {
							const parsedError = JSON.parse(dataStr);
							errorMessage = parsedError?.message || parsedError?.error || errorMessage;
						} catch {
							// dataStr bukan JSON — pakai pesan default
						}
					}
					onError(new Error(errorMessage));
					return;
				}

				// Tool call lifecycle — jangan treat sebagai token konten
				if (eventKey === "toolcallstarted" || eventKey === "toolcallcompleted") {
					if (dataStr && onToolEvent) {
						try {
							const parsed = JSON.parse(dataStr);
							const tool_name = String(parsed?.tool_name || "").trim();
							const tool_call_id = String(parsed?.tool_call_id || tool_name || Date.now()).trim();
							if (tool_name || tool_call_id) {
								onToolEvent({
									type: eventKey === "toolcallstarted" ? "started" : "completed",
									tool_name: tool_name || "tool",
									tool_call_id,
									success: typeof parsed?.success === "boolean" ? parsed.success : undefined,
								});
							}
						} catch {
							// payload tool invalid — skip
						}
					}
					continue;
				}

				// Handle [DONE] marker (fallback format OpenAI)
				if (dataStr === "[DONE]") {
					onComplete();
					return;
				}

				if (!dataStr) continue;

				try {
					const parsed = JSON.parse(dataStr);

					// Defensive: object error (field error/message non-kosong) — jangan perlakukan sebagai token
					// Format token asli hanya pakai content/token/choices/response, bukan message/error
					// Jangan treat object tool payload sisa (tool_name) sebagai error
					if (parsed?.tool_name || parsed?.tool_call_id) {
						continue;
					}

					const errMsg = parsed?.error || parsed?.message;
					if (typeof errMsg === "string" && errMsg.trim()) {
						onError(new Error(errMsg));
						return;
					}

					// Extract token dari berbagai format yang mungkin
					// Format asli (API Brodi): { content: "..." }
					// Format 1: { token: "..." }
					// Format 2: { choices: [{ delta: { content: "..." } }] } (OpenAI style)
					// Format 3: { response: "..." } (fallback non-stream di dalam stream)
					const tokenText =
						parsed?.content ??
						parsed?.token ??
						parsed?.choices?.[0]?.delta?.content ??
						parsed?.response ??
						"";

					if (tokenText) {
						onChunk(tokenText);
					}
				} catch {
					// Ignore parse error untuk chunk yang tidak valid JSON
					// Bisa jadi keep-alive comment atau data kosong
				}
			}
		}

		// Stream selesai tanpa [DONE] marker
		onComplete();
	} catch (error) {
		// AbortError tidak dianggap error, biarkan dipanggil onComplete/cleanup di caller
		if (error instanceof DOMException && error.name === "AbortError") {
			return;
		}
		onError(error instanceof Error ? error : new Error(String(error)));
	}
};
